// src/pages/CRMContactDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contactsAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Edit2, Save, X, Mail, Phone, Building2,
  MapPin, User, Briefcase, FileText, Tag,
} from 'lucide-react';
import TracabilityInfo from '../components/common/TracabilityInfo';

const STATUS_META = {
  INTERLOCUTEUR: { label: 'Interlocuteur', color: '#3B82F6' },
  DECISIONNAIRE: { label: 'Décisionnaire', color: '#8B5CF6' },
  PROSPECT:      { label: 'Prospect',       color: '#F59E0B' },
  INACTIF:       { label: 'Inactif',        color: '#6B7280' },
};

const EDITABLE_FIELDS = [
  { key: 'job_title',           label: 'Poste / Titre' },
  { key: 'service',             label: 'Service' },
  { key: 'status',              label: 'Statut',    type: 'select', options: Object.entries(STATUS_META).map(([v,m]) => ({ value: v, label: m.label })) },
  { key: 'email',               label: 'Email principal' },
  { key: 'email_2',             label: 'Email secondaire' },
  { key: 'phone',               label: 'Téléphone' },
  { key: 'phone_2',             label: 'Téléphone 2' },
  { key: 'address',             label: 'Adresse' },
  { key: 'city',                label: 'Ville' },
  { key: 'postal_code',         label: 'Code postal' },
  { key: 'country',             label: 'Pays' },
  { key: 'perimetre_technique', label: 'Périmètre technique' },
  { key: 'notes',               label: 'Notes', type: 'textarea' },
];

function InfoRow({ icon: Icon, label, value, href }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <Icon size={15} style={{ color: 'var(--text-muted)', marginTop: 2, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{label}</div>
        {href
          ? <a href={href} style={{ color: '#3B82F6', textDecoration: 'none', fontSize: '0.875rem' }}>{value}</a>
          : <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>{value}</div>
        }
      </div>
    </div>
  );
}

export default function CRMContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await contactsAPI.get(id);
        setContact(res.data);
        setForm(res.data);
      } catch {
        toast.error('Contact introuvable');
        navigate('/crm-contacts');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      EDITABLE_FIELDS.forEach(f => { payload[f.key] = form[f.key] ?? null; });
      const res = await contactsAPI.update(id, payload);
      setContact(res.data);
      setForm(res.data);
      setEditing(false);
      toast.success('Contact mis à jour');
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px', color: 'var(--text-muted)' }}>
        <div className="spinner" style={{ marginRight: 10 }}/> Chargement…
      </div>
    );
  }
  if (!contact) return null;

  const sm = STATUS_META[contact.status] || { label: contact.status, color: '#6B7280' };
  const initials = (contact.full_name || '?').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <button className="btn btn-ghost" onClick={() => navigate('/crm-contacts')} style={{ marginBottom: 6 }}>
            <ArrowLeft size={15}/> Contacts
          </button>
          <h1 style={{ margin: 0 }}>{contact.full_name || '—'}</h1>
          {contact.job_title && <p style={{ margin: 0, color: 'var(--text-muted)' }}>{contact.job_title}</p>}
        </div>
        <div className="page-header-actions">
          {editing ? (
            <>
              <button className="btn btn-secondary" onClick={() => { setEditing(false); setForm(contact); }} disabled={saving}>
                <X size={14}/> Annuler
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <div className="spinner" style={{ width: 13, height: 13 }}/> : <Save size={14}/>} Enregistrer
              </button>
            </>
          ) : (
            <button className="btn btn-primary" onClick={() => setEditing(true)}>
              <Edit2 size={14}/> Modifier
            </button>
          )}
        </div>
      </div>

      {/* Traçabilité */}
      <div style={{ marginBottom: 16 }}>
        <TracabilityInfo
          createdAt={contact.created_at}
          createdById={contact.created_by_user_id || contact.owner_user_id}
          updatedAt={contact.updated_at}
          updatedById={contact.updated_by_user_id}
          showWarningIfNoData
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20 }}>
        {/* Carte identité */}
        <div>
          <div className="card" style={{ padding: '24px', textAlign: 'center', marginBottom: 16 }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: 'linear-gradient(135deg, #1D4ED8, #3B82F6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.5rem', fontWeight: 700, color: '#fff',
              margin: '0 auto 12px',
            }}>{initials}</div>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: 4 }}>{contact.full_name}</div>
            {contact.civilite && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 8 }}>{contact.civilite}</div>}
            <span style={{ whiteSpace: 'nowrap',
              fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: 6,
              color: sm.color, background: `${sm.color}18`,
            }}>{sm.label}</span>
            {contact.company_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', marginTop: 12, fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                <Building2 size={13}/>{contact.company_name}
              </div>
            )}
          </div>

          {/* Coordonnées */}
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Coordonnées</div>
            <InfoRow icon={Mail}      label="Email"        value={contact.email}   href={contact.email ? `mailto:${contact.email}` : null}/>
            <InfoRow icon={Mail}      label="Email 2"      value={contact.email_2} href={contact.email_2 ? `mailto:${contact.email_2}` : null}/>
            <InfoRow icon={Phone}     label="Téléphone"    value={contact.phone}   href={contact.phone ? `tel:${contact.phone}` : null}/>
            <InfoRow icon={Phone}     label="Téléphone 2"  value={contact.phone_2} href={contact.phone_2 ? `tel:${contact.phone_2}` : null}/>
            <InfoRow icon={MapPin}    label="Adresse"      value={[contact.address, contact.postal_code, contact.city, contact.country].filter(Boolean).join(', ')}/>
          </div>
        </div>

        {/* Détails + édition */}
        <div>
          {editing ? (
            <div className="card" style={{ padding: '20px 24px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>Modifier le contact</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
                {EDITABLE_FIELDS.map(f => (
                  <div key={f.key} style={f.type === 'textarea' ? { gridColumn: 'span 2' } : {}}>
                    <label style={lbl}>{f.label}</label>
                    {f.type === 'select' ? (
                      <select style={inp} value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}>
                        <option value="">—</option>
                        {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : f.type === 'textarea' ? (
                      <textarea style={{ ...inp, minHeight: 80, resize: 'vertical' }}
                        value={form[f.key] || ''}
                        onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}/>
                    ) : (
                      <input style={inp} value={form[f.key] || ''}
                        onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}/>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="card" style={{ padding: '16px 20px', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Informations professionnelles</div>
                <InfoRow icon={Briefcase} label="Poste"            value={contact.job_title}/>
                <InfoRow icon={Tag}       label="Service"          value={contact.service}/>
                <InfoRow icon={User}      label="Manager"          value={contact.manager_name}/>
                <InfoRow icon={Building2} label="Agence"           value={contact.agency}/>
                <InfoRow icon={Tag}       label="Type"             value={contact.contact_type}/>
                {contact.perimetre_technique && (
                  <div style={{ padding: '8px 0' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Périmètre technique</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{contact.perimetre_technique}</div>
                  </div>
                )}
                {contact.domains && (
                  <InfoRow icon={Tag} label="Domaines" value={Array.isArray(contact.domains) ? contact.domains.join(', ') : contact.domains}/>
                )}
              </div>

              {contact.notes && (
                <div className="card" style={{ padding: '16px 20px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <FileText size={13} style={{ verticalAlign: 'middle', marginRight: 5 }}/>Notes
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{contact.notes}</div>
                </div>
              )}

              {/* Timestamps */}
              <div style={{ marginTop: 12, fontSize: '0.72rem', color: 'var(--text-faint)' }}>
                {contact.boond_ref && <span style={{ marginRight: 16 }}>Réf. Boond: {contact.boond_ref}</span>}
                {contact.created_at && <span>Créé le {new Date(contact.created_at).toLocaleDateString('fr-FR')}</span>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const lbl = {
  display: 'block', fontSize: '0.72rem', fontWeight: 600,
  color: 'var(--text-muted)', marginBottom: 4,
  textTransform: 'uppercase', letterSpacing: '0.04em',
};
const inp = {
  width: '100%', padding: '8px 10px',
  border: '1.5px solid var(--border)', borderRadius: 7,
  fontSize: '0.875rem', boxSizing: 'border-box',
  fontFamily: 'inherit', outline: 'none',
  background: 'var(--bg-elevated)', color: 'var(--text-primary)',
};
